import { inject, Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject, of, forkJoin } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { GroupMetadata } from '../interfaces/group-metadata';
import { ImpersonatedFilterConfiguration } from '../models/ImpersonatedFilterConfiguration';
import { AuthenticationService } from './authentication.service';
import { knownFeatures } from '../constants/known-features';
import { ArrayHelper } from '../../shared/utils/ArrayHelper';
import { CustomerService } from './customer.service';
import { CustomerHeader } from '../interfaces/customer-header';
import { CustomerMetadata } from '../interfaces/customer-metadata';
import { CustomerSearchFilter } from '../interfaces/customer-search-filter';
import { TenantService } from './tenant.service';

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly customerService = inject(CustomerService);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _tenantService = inject(TenantService);

  public isInitialized = false;

  private readonly _impersonatedGroupSource = new BehaviorSubject<CustomerMetadata>(null);
  impersonatedGroup = this._impersonatedGroupSource.asObservable();

  private readonly _isBusySource = new BehaviorSubject(false);
  isBusy = this._isBusySource.asObservable();

  private readonly _recentGroupsSource = new BehaviorSubject<CustomerHeader[]>(null);
  recentGroups = this._recentGroupsSource.asObservable();

  private readonly _currentTenantSource = new BehaviorSubject<string>(null);
  currentTenant = this._currentTenantSource.asObservable();

  private readonly _numberOfAccessibleGroupsSource = new BehaviorSubject(0);
  numberOfAccessibleGroups = this._numberOfAccessibleGroupsSource.asObservable();

  private readonly _unsubscribe: Subject<void> = new Subject();
  private readonly _initializationSubject: Subject<void> = new Subject();

  constructor() {
    this.fetchTenants();
  }

  public initializeAsync(): Observable<void> {
    if (this.isInitialized) return of(null);
    this.startLoadRecentUserGroups();
    this.startLoadTenant();
    return this._initializationSubject.asObservable();
  }

  public getCurrentGroupIdentifier(): string {
    return this._recentGroupsSource.value?.[0]?.id;
  }

  public getCurrentTenantIdentifier(): string {
    const platformTenantId = '_Platform_';
    const defaultTenantId = 'GB';
    let currentTenantId = defaultTenantId;
    const lastAccessedTenantId = localStorage.getItem('user.tenant');

    if (lastAccessedTenantId == null) {
      const currentUser = this._authenticationService.getCurrentUser();
      currentTenantId = currentUser?.tenantId ?? null;
      if (currentTenantId == null || currentTenantId === platformTenantId) {
        currentTenantId = defaultTenantId;
      }
    } else {
      currentTenantId = lastAccessedTenantId;
    }
    return currentTenantId;
  }

  public getBrowserLanguage(): string {
    return navigator.language || (navigator as any).userLanguage;
  }

  public getImpersonatedCustomerIdentifier(): string {
    const group = this.getImpersonatedGroup();
    return group?.externalId ?? null;
  }

  public getImpersonatedGroup(): any {
    return this._impersonatedGroupSource.value;
  }

  public getAvailableContracts(): Array<string> {
    const currentGroup = this.getImpersonatedGroup();
    if (currentGroup?.contractedUnits) {
      return ArrayHelper.distinctValues(currentGroup.contractedUnits.map(c => c.contractNumber));
    }
    return [];
  }

  public getFilterConfiguration(): ImpersonatedFilterConfiguration {
    return new ImpersonatedFilterConfiguration(this.getImpersonatedGroup());
  }

  public isFeatureActiveForCurrentGroup(featureKey: string): boolean {
    return this.isFeatureActiveForGroup(this.getImpersonatedGroup(), featureKey);
  }

  public isAnyMaxFeatureActiveForCurrentGroup(impersonatedGroup: GroupMetadata) {
    const maxFeatureKeys = [
      knownFeatures.statisticMaxUptimeScore,
      knownFeatures.statisticEscalatorEnergyConsumption,
      knownFeatures.statisticEscalatorMaxUptimeScore,
      knownFeatures.statisticEscalatorPeopleCounter,
      knownFeatures.statisticEscalatorPowerConsumption,
      knownFeatures.statisticEscalatorStatusCount,
      knownFeatures.statisticEscalatorStatusDuration,
      knownFeatures.statisticElevatorCallDirections,
      knownFeatures.statisticElevatorCallWaitTime,
      knownFeatures.statisticElevatorDoorMovements,
      knownFeatures.statisticElevatorTraveledDistance,
      knownFeatures.statisticElevatorTraveledTime,
      knownFeatures.statisticElevatorTripCount,
    ];
    return maxFeatureKeys.some(feature => this.isFeatureActiveForGroup(impersonatedGroup, feature));
  }

  public isFeatureActiveForGroup(group: GroupMetadata, featureKey: string): boolean {
    return !!group?.featureTier.features.some((f: string) => f === featureKey);
  }

  public isFeatureActiveForAnyGroup(featureKey: string): boolean {
    return this.isFeatureActiveForCurrentGroup(featureKey);
  }

  public reloadGroups(): Observable<void> {
    this.startLoadRecentUserGroups();
    return this._initializationSubject.asObservable();
  }

  public setCurrentTenantIdentifier(tenant: string): void {
    if (tenant) {
      localStorage.setItem('user.tenant', tenant);
      this._currentTenantSource.next(tenant);
    } else {
      localStorage.removeItem('user.tenant');
      this._currentTenantSource.next(null);
    }
  }

  public setCurrentGroupValue(group) {
    const currentGroupSource = this._recentGroupsSource.value;
    currentGroupSource.unshift(group);
    this._recentGroupsSource.next(
      [group, ...this._recentGroupsSource.value.filter(c => c.id !== group.id)].slice(0, 10)
    );
    this.processGroupMetadata(group);
  }

  private shiftRecentGroupsIfSelectionIsNotPresentYet(selectedGroup): void {
    const recentGroups = this._recentGroupsSource.value;
    if (!recentGroups) return;

    if (recentGroups.some(g => g.id === selectedGroup.id)) {
      const index = this._recentGroupsSource.value.findIndex(g => g.id === selectedGroup.id);
      recentGroups.splice(index, 1);
    } else {
      recentGroups.pop();
    }
    recentGroups.unshift({ ...selectedGroup });
    this._recentGroupsSource.next(recentGroups);
  }

  public setCurrentGroup() {
    this._isBusySource.next(true);
    const currentGroup = this._recentGroupsSource.value[0];
    this.processGroupMetadata(currentGroup);
    this._isBusySource.next(false);
  }

  private processGroupMetadata(metadata): void {
    if (metadata) {
      this.setCurrentTenantIdentifier(this.getCurrentTenantIdentifier());
      this.shiftRecentGroupsIfSelectionIsNotPresentYet(metadata);
      sessionStorage.setItem('user.group', metadata.id.toString());
      this.customerService
        .setLastAccessedCustomer(metadata.id, metadata.tenantId, metadata.clientType)
        .pipe(takeUntil(this._unsubscribe))
        .subscribe();
      this._impersonatedGroupSource.next(metadata);
      this.markAsInitialized();
    }
  }

  private processRecentGroups(recentGroups: CustomerHeader[]): void {
    this._recentGroupsSource.next(recentGroups);
    const currentGroupId = this.getCurrentGroupIdentifier();
    recentGroups.length > 0 || currentGroupId ? this.setCurrentGroup() : this.markAsInitialized();
  }

  private markAsInitialized(): void {
    this.isInitialized = true;
    this._initializationSubject.next();
  }

  private startLoadTenant(): void {
    const tenant = this.getCurrentTenantIdentifier();
    this._currentTenantSource.next(tenant);
  }

  private startLoadRecentUserGroups(): void {
    this._unsubscribe.next();
    this.isInitialized = false;
    const groupDataOperations = forkJoin([
      this.customerService.getCustomerMasterCount({
        tenantId: this.getCurrentTenantIdentifier(),
      } as CustomerSearchFilter),
      this.customerService.getLastAccessedCustomer({ take: 10 }),
    ]);

    groupDataOperations.pipe(takeUntil(this._unsubscribe)).subscribe(loaderResult => {
      this._numberOfAccessibleGroupsSource.next(loaderResult[0]);
      this.processRecentGroups(loaderResult[1]);
    });
  }

  private fetchTenants(): void {
    this._impersonatedGroupSource
      .pipe(
        filter(group => !!group),
        switchMap(() => this._tenantService.getTenants())
      )
      .subscribe();
  }

  public getCurrentGroup(): CustomerHeader {
    return this._recentGroupsSource.value[0];
  }
}
